import { createSlice } from "@reduxjs/toolkit";
import {
  lapFilters,
  virtualLoopsData,
  virtualLoopsMessage,
  lapData,
} from "../types/virtualLoops";

const initialState = {
  busy: false,
  data: {},
  path: "",
  cornerConfig: {},
  lapFilters: {
    session: "all",
    powerMode: "all",
    driver: "all",
    pushLapsOnly: false,
    selectedSwitches: [],
    outInLaps: true,
  },
  filteredData: {},
  highlightedLoop: "",
  configLapData: {
    vCar: [],
    sLap: [],
  },
  generation: "Gen3",
} as virtualLoopsMessage;

function applyLapFilters(
  data: virtualLoopsData,
  filters: lapFilters,
  generation?: string,
): virtualLoopsData {
  const filteredData: virtualLoopsData = {};

  const fastestLapsBySession: Record<string, number> = {};
  for (const key in data) {
    for (const lapData of data[key]) {
      const sessionName = lapData.session;
      if (
        !fastestLapsBySession[sessionName] ||
        (lapData.lapTime < fastestLapsBySession[sessionName] && lapData.real)
      ) {
        fastestLapsBySession[sessionName] = lapData.lapTime;
      }
    }
  }

  for (const key in data) {
    let fastestLapTimeAll = Infinity;
    for (const lapData of data[key]) {
      if (lapData.lapTime < fastestLapTimeAll && lapData.real) {
        fastestLapTimeAll = lapData.lapTime;
      }
    }

    filteredData[key] = data[key].filter((lapData: lapData) => {
      const isSelectedSwitch = filters.selectedSwitches.includes(
        lapData.goSwitch,
      );
      const matchesOutInLaps =
        filters.outInLaps === true || (!lapData.inLap && !lapData.outLap);
      const matchesSession =
        filters.session === "all" || lapData.session === filters.session;
      const matchesPowerMode =
        filters.powerMode === "all" || lapData.powerMode === filters.powerMode;
      const matchesDriver =
        filters.driver === "all" || lapData.driver === filters.driver;

      let validPushSwitches: number[] = [];
      if (generation === "Gen4") {
        validPushSwitches = [4, 5, 6, 7, 8, 9, 10, 11];
      } else {
        validPushSwitches = [6, 7, 8];
      }

      const matchesPushLapsOnly =
        filters.pushLapsOnly === false ||
        (lapData.lapTime <= fastestLapTimeAll * 1.1 &&
          lapData.lapTime <= fastestLapsBySession[lapData.session] * 1.05 &&
          validPushSwitches.includes(lapData.goSwitch) &&
          !lapData.inLap &&
          !lapData.outLap &&
          lapData.real);

      return (
        isSelectedSwitch &&
        matchesSession &&
        matchesPowerMode &&
        matchesDriver &&
        matchesPushLapsOnly &&
        matchesOutInLaps
      );
    });
  }
  return filteredData;
}

export const virtualLoopsSlice = createSlice({
  name: "virtualLoops",
  initialState,
  reducers: {
    setVirtualLoopsData: (state, action) => {
      const virtualLoopsData = action.payload;

      if (virtualLoopsData.busy !== undefined) {
        state.busy = virtualLoopsData.busy;
      }

      if (virtualLoopsData.data) {
        const transformedData: virtualLoopsData = {};
        for (const sessionData of virtualLoopsData.data) {
          transformedData[sessionData.SSN] = sessionData.data.map(
            (lapData: lapData) => ({
              ...lapData,
              virtualLoops: Array.isArray(lapData.virtualLoops)
                ? lapData.virtualLoops.reduce(
                    (
                      acc: Record<string, number>,
                      loop: { loopName: string; loopTime: number },
                    ) => {
                      acc[loop.loopName] = loop.loopTime;
                      return acc;
                    },
                    {},
                  )
                : {},
            }),
          );
        }
        state.data = transformedData;
      }

      if (virtualLoopsData.configLapData) {
        state.configLapData = virtualLoopsData.configLapData;
      }
      if (virtualLoopsData.config) {
        state.path = virtualLoopsData.config.path;
        state.generation = virtualLoopsData.config.generation;
        state.cornerConfig = virtualLoopsData.config.cornerConfig.reduce(
          (
            acc: Record<string, string | number>,
            corner: { cornerName: string; cornerValue: string },
          ) => {
            // Try to convert to number if it's a numeric string
            const numericValue = Number(corner.cornerValue);
            acc[corner.cornerName] =
              !isNaN(numericValue) && corner.cornerValue.trim() !== ""
                ? numericValue
                : corner.cornerValue;
            return acc;
          },
          {},
        );
      }
      if (state.data == null || state.lapFilters == null) {
        state.filteredData = {};
        return;
      }
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },

    setSessionFilters: (state, action) => {
      if (state.lapFilters == null || state.data == null) {
        state.lapFilters = initialState.lapFilters;
        return;
      }
      state.lapFilters.session = action.payload;
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },
    setPowerModeFilters: (state, action) => {
      if (state.lapFilters == null || state.data == null) {
        state.lapFilters = initialState.lapFilters;
        return;
      }
      state.lapFilters.powerMode = action.payload;
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },
    setDriverFilters: (state, action) => {
      if (state.lapFilters == null || state.data == null) {
        state.lapFilters = initialState.lapFilters;
        return;
      }
      state.lapFilters.driver = action.payload;
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },
    setPushLapsOnlyFilter: (state, action) => {
      if (state.lapFilters == null || state.data == null) {
        state.lapFilters = initialState.lapFilters;
        return;
      }
      state.lapFilters.pushLapsOnly = action.payload;
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },
    setHighlightedLoop: (state, action) => {
      state.highlightedLoop = action.payload;
    },
    setSelectedSwitches: (state, action) => {
      if (state.lapFilters == null || state.data == null) {
        state.lapFilters = initialState.lapFilters;
        return;
      }
      state.lapFilters.selectedSwitches = action.payload;
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },
    setOutInLaps: (state, action) => {
      if (state.lapFilters == null || state.data == null) {
        state.lapFilters = initialState.lapFilters;
        return;
      }
      state.lapFilters.outInLaps = action.payload;
      state.filteredData = applyLapFilters(
        state.data,
        state.lapFilters,
        state.generation,
      );
    },
  },
});

export const {
  setVirtualLoopsData,
  setPowerModeFilters,
  setDriverFilters,
  setSessionFilters,
  setPushLapsOnlyFilter,
  setHighlightedLoop,
  setSelectedSwitches,
  setOutInLaps,
} = virtualLoopsSlice.actions;
export default virtualLoopsSlice.reducer;
